import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../utils/firebase";

// Override manual de Customer No / color para códigos que Finnegans no tiene bien
// cargados en el catálogo (o que directamente no están). Se mezcla por sobre lo
// que devuelva el catálogo, así el nombre sale con nuestro código y no el de Finnegans.
const CATALOGO_OVERRIDE = {
  "582028111": { customerNo: "2811 SMALL", finish: "Anodizado" },
  "582028113": { customerNo: "2811 SMALL", finish: "Anolok" },
  // 502002091 (Paño Fijo Lateral S20 Anodizado) vino con CustomerNo 2326 (choca con
  // el Toallero Mampara). El correcto es 209 (igual que su hermano Blanco 502002094).
  "502002091": { customerNo: "209" },
};

// ---------------------------------------------------------------------------
// Caché en memoria del catálogo.
//
// Antes, cada vez que se abría un pedido se leía el catálogo producto por
// producto desde Firestore (una consulta a internet por cada código), aunque
// fuera el mismo perfil que ya se había buscado hace segundos en otro pedido.
// Eso hacía lento abrir pedidos y "reconvertía" Uru -> Customer No todo el tiempo.
//
// Ahora guardamos el resultado (incluido null cuando el producto no existe) en
// un Map en memoria. La primera búsqueda de un código va a internet; las
// siguientes salen instantáneas, en cualquier pedido/pantalla, mientras dure la
// sesión. El catálogo casi no cambia durante el día, así que es seguro.
//
// Nota: el Map guarda una PROMESA, no el valor. Así, si dos pedidos piden el
// mismo código a la vez (o el mismo pedido lo pide varias veces), se hace una
// sola lectura y las demás esperan el mismo resultado (dedupe de peticiones).
// ---------------------------------------------------------------------------
const _cache = new Map();

function _aplicarOverride(key, base) {
  const override = CATALOGO_OVERRIDE[key];
  if (override) return { ...(base || {}), ...override };
  return base;
}

// ── Tubos vs Ángulos ────────────────────────────────────────────────────────
// Tubos y ángulos tienen la misma medida (ej. "19x19") y el operario no sabe cuál
// es. Prefijamos el Customer No con "TUBO " o "ANG " según el código. Los sets son
// los códigos SIN el último dígito (que es el color): así aplica a todos los colores.
const TUBOS_BASE = new Set([
  "60131901", "60132501", "60135001", "60152010", "60155001", "60157510",
  "60157550", "60157551", "60159930", "60159950", "60159951", "64152010", "65152010",
]);
const ANGULOS_BASE = new Set([
  "60001001", "60001501", "60001901", "60002501", "60003001", "60004001", "60005001",
]);

function _prefijoTuboAngulo(key, base) {
  if (!base || !base.customerNo) return base;
  const cn = String(base.customerNo);
  if (/^\s*(TUBO|ANG)\b/i.test(cn)) return base; // ya viene prefijado
  const sinColor = String(key).slice(0, -1); // el último dígito es el color
  if (TUBOS_BASE.has(sinColor)) return { ...base, customerNo: `TUBO ${cn}` };
  if (ANGULOS_BASE.has(sinColor)) return { ...base, customerNo: `ANG ${cn}` };
  return base;
}

// Devuelve { bundles, pieces, finish, customerNo, ... } o null
export function getCatalogoByCodUru(codUru) {
  if (!codUru) return Promise.resolve(null);
  const key = String(codUru);

  if (_cache.has(key)) return _cache.get(key);

  const promesa = (async () => {
    try {
      const snap = await getDoc(doc(db, "catalogoProductos", key));
      const base = snap.exists() ? snap.data() : null;
      return _prefijoTuboAngulo(key, _aplicarOverride(key, base));
    } catch (e) {
      // No cachear el error: lo borramos para poder reintentar la próxima vez.
      _cache.delete(key);
      throw e;
    }
  })();

  _cache.set(key, promesa);
  return promesa;
}

// Limpia la caché (por si en algún flujo administrativo se edita el catálogo y
// se necesita releer sin recargar la app). Sin argumento borra todo; con un
// código borra solo esa entrada.
export function invalidarCatalogo(codUru) {
  if (codUru == null) { _cache.clear(); return; }
  _cache.delete(String(codUru));
}
